import React, { useEffect, useState } from "react";
import {
  MapPin,
  Plus,
  Edit2,
  Trash2,
  X,
  Star,
  Phone,
  Home,
} from "lucide-react";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import ConfirmModal from "../components/Modals/Confirm";
import ContactInfo from "../components/ContactInfo";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../components/LoadingComponent";

interface Address {
  id: number;
  street: string;
  // apartment: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  fullName: string;
  phone: string;
  isDefault: boolean;
  // Qatar-specific fields
  zone?: string;
  buildingNumber?: string;
  unitNumber?: string;
}

export interface AddressFormData {
  street: string;
  city: string;
  country: string;
  fullName: string;
  phone: string;
  isDefault: boolean;
  // Qatar-specific fields
  zone?: string;
  buildingNumber?: string;
  unitNumber?: string;
}

interface ValidationErrors {
  street?: string;
  // apartment?: string;
  city?: string;
  // state?: string;
  country?: string;
  // zipCode?: string;
  fullName?: string;
  phone?: string;
  zone?: string;
  buildingNumber?: string;
  unitNumber?: string;
}

export interface Country {
  code: string;
  country: string;
  name: string;
  flag: string;
}

// Supported countries
export const COUNTRIES: Country[] = [
  { code: "+971", country: "AE", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "+968", country: "OM", name: "Oman", flag: "🇴🇲" },
  { code: "+973", country: "BH", name: "Bahrain", flag: "🇧🇭" },
  { code: "+965", country: "KW", name: "Kuwait", flag: "🇰🇼" },
  { code: "+966", country: "SA", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+974", country: "QA", name: "Qatar", flag: "🇶🇦" },
  { code: "+91", country: "IN", name: "India", flag: "🇮🇳" },
];

// States/Emirates by country
// const STATES_BY_COUNTRY: Record<string, string[]> = {
//   "AE": ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah", "other"],
//   "OM": ["Muscat", "Dhofar", "Musandam", "Al Buraimi", "Ad Dakhiliyah", "Al Batinah North", "Al Batinah South", "Ash Sharqiyah North", "Ash Sharqiyah South", "Al Dhahirah", "Al Wusta", "other"],
//   "BH": ["Capital Governorate", "Muharraq Governorate", "Northern Governorate", "Southern Governorate", "other"],
//   "KW": ["Al Asimah", "Hawalli", "Farwaniya", "Jahra", "Ahmadi", "Mubarak Al-Kabeer", "other"],
//   "SA": ["Riyadh", "Makkah", "Madinah", "Eastern Province", "Asir", "Tabuk", "Qassim", "Ha'il", "Jizan", "Najran", "Al Bahah", "Northern Borders", "Jawf", "Al-Qurayyat", "other"],
//   "QA": ["Doha", "Al Rayyan", "Umm Salal", "Al Khor", "Al Wakrah", "Al Daayen", "Madinat ash Shamal", "Al Shahaniya", "Al-Shahaniya", "other"],
//   "IN": ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh", "other"],
// };

// Major cities by country (you can expand these lists)
export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  AE: [
    "Dubai",
    "Abu Dhabi",
    "Sharjah",
    "Ajman",
    "Ras Al Khaimah",
    "Fujairah",
    "Umm Al Quwain",
    "Al Ain",
    "other",
  ],
  OM: [
    "Muscat",
    "Salalah",
    "Sohar",
    "Nizwa",
    "Sur",
    "Ibri",
    "Buraimi",
    "other",
  ],
  BH: [
    "Manama",
    "Muharraq",
    "Riffa",
    "Hamad Town",
    "Isa Town",
    "Sitra",
    "other",
  ],
  KW: [
    "Kuwait City",
    "Hawalli",
    "Salmiya",
    "Farwaniya",
    "Ahmadi",
    "Jahra",
    "other",
  ],
  SA: [
    "Riyadh",
    "Jeddah",
    "Mecca",
    "Medina",
    "Dammam",
    "Khobar",
    "Dhahran",
    "Tabuk",
    "Abha",
    "other",
  ],
  QA: [
    "Doha",
    "Al Rayyan",
    "Al Wakrah",
    "Al Khor",
    "Umm Salal",
    "Al Daayen",
    "Madinat ash Shamal",
    "Al Shahaniya",
    "Lusail",
    "Mesaieed",
    "Dukhan",
    "Al Ruwais",
    "other",
  ],
  IN: [
    "Mumbai",
    "Delhi",
    "Bangalore",
    "Hyderabad",
    "Chennai",
    "Kolkata",
    "Pune",
    "Ahmedabad",
    "Jaipur",
    "Lucknow",
    "Nagpur",
    "other",
  ],
};

// Zip codes by country (sample data - you can expand)
// const ZIPCODES_BY_COUNTRY: Record<string, string[]> = {
//   "AE": ["00000", "12345", "54321", "other"],
//   "OM": ["100", "111", "112", "113", "114", "115", "other"],
//   "BH": ["111", "199", "299", "399", "499", "other"],
//   "KW": ["10000", "20000", "30000", "40000", "50000", "60000", "other"],
//   "SA": ["11111", "12345", "21111", "23000", "other"],
//   "QA": ["00000", "12345", "54321", "other"],
//   "IN": ["110001", "400001", "560001", "500001", "600001", "700001", "440001", "other"],
// };

const AddressManagement: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [defaultAddress, setDefaultAddress] = useState<Address>();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editingAddress, setEditingAddress] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState<AddressFormData>({
    street: "",
    city: "",
    country: "QA",
    fullName: "",
    phone: "",
    isDefault: false,
    // Qatar-specific fields
    zone: "",
    buildingNumber: "",
    unitNumber: "",
  });

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const { success, err } = useToast();

  // Track manual input mode for each field
  const [isManualZip, setIsManualZip] = useState<boolean>(false);
  const [isManualCity, setIsManualCity] = useState<boolean>(false);
  const [isManualState, setIsManualState] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get available options based on selected country
  // const availableStates = STATES_BY_COUNTRY[formData.country] || [];
  const availableCities = CITIES_BY_COUNTRY[formData.country] || [];
  // const availableZipCodes = ZIPCODES_BY_COUNTRY[formData.country] || [];

  const resetForm = (): void => {
    setFormData({
      street: "",
      // apartment: "",
      city: "",
      // state: "",
      country: "QA",
      // zipCode: "",
      fullName: "",
      phone: "",
      isDefault: false,
      // Qatar-specific fields
      zone: "",
      buildingNumber: "",
      unitNumber: "",
    });
    setErrors({});
    setIsManualZip(false);
    setIsManualCity(false);
    setIsManualState(false);
  };

  useEffect(() => {
    fetchAddresses();
    fetchDefaultAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const res = await BaseApi.get(
        `/addresses?page=${currentPage}&limit=${itemsPerPage}`,
      );
      if (res?.data) setAddresses(res.data);
    } catch (error) {
      console.log("Failed to fetch addresses", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDefaultAddress = async () => {
    try {
      const res = await BaseApi.get("/addresses/default");
      if (res?.data) setDefaultAddress(res.data);
    } catch (error) {
      console.log("Failed to fetch default address", error);
    }
  };

  const openAddModal = () => {
    resetForm();
    setEditingAddress(null);
    setShowAddModal(true);
  };

  const openEditModal = (address: Address) => {
    setFormData({
      street: address.street || "",
      city: address.city || "",
      country: address.country || "QA",
      fullName: address.fullName || "",
      phone: address.phone || "",
      isDefault: address.isDefault || false,
      zone: address.zone || "",
      buildingNumber: address.buildingNumber || "",
      unitNumber: address.unitNumber || "",
    });
    setEditingAddress(address.id);

    // Check if values are custom (not in predefined lists)
    // const statesList = STATES_BY_COUNTRY[address.country] || [];
    const citiesList = CITIES_BY_COUNTRY[address.country] || [];
    // const zipsList = ZIPCODES_BY_COUNTRY[address.country] || [];

    // setIsManualState(!statesList.includes(address.state));
    setIsManualCity(!citiesList.includes(address.city));
    // setIsManualZip(!zipsList.includes(address.zipCode));

    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingAddress(null);
    resetForm();
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    const isQatar = formData.country === "QA";

    // Apartment validation (not required for Qatar if zone and building number are provided)
    // if (!isQatar && !formData.apartment.trim()) {
    //   newErrors.apartment = t("Apartment is required");
    // } else if (formData.apartment.trim() && formData.apartment.trim().length > 50) {
    //   newErrors.apartment = t("Apartment must not exceed 50 characters");
    // }

    // Qatar-specific validations
    if (isQatar) {
      // Zone validation (mandatory for Qatar)
      if (!formData.zone?.trim()) {
        newErrors.zone = t("Zone is required");
      } else if (formData.zone.trim().length < 1) {
        newErrors.zone = t("Zone must be at least 1 character");
      } else if (formData.zone.trim().length > 10) {
        newErrors.zone = t("Zone must not exceed 10 characters");
      } else if (!/^[0-9]+$/.test(formData.zone.trim())) {
        newErrors.zone = t("Zone must contain only numbers");
      }

      // Building Number validation (mandatory for Qatar)
      if (!formData.buildingNumber?.trim()) {
        newErrors.buildingNumber = t("Building Number is required");
      } else if (formData.buildingNumber.trim().length < 1) {
        newErrors.buildingNumber = t(
          "Building Number must be at least 1 character",
        );
      } else if (formData.buildingNumber.trim().length > 10) {
        newErrors.buildingNumber = t(
          "Building Number must not exceed 10 characters",
        );
      } else if (!/^[0-9]+$/.test(formData.buildingNumber.trim())) {
        newErrors.buildingNumber = t(
          "Building Number must contain only numbers",
        );
      }

      // Unit Number validation (optional for Qatar)
      // Unit Number validation (optional for Qatar)
      // if (!formData.unitNumber?.trim()) {
      //   newErrors.unitNumber = t("Unit Number is required");
      // } else if (formData.unitNumber.trim().length < 1) {
      //   newErrors.unitNumber = t("Unit Number must be at least 1 character");
      // } else
      if (formData.unitNumber && formData.unitNumber?.trim().length > 10) {
        newErrors.unitNumber = t("Unit Number must not exceed 10 characters");
      } else if (
        formData.unitNumber &&
        !/^[0-9]+$/.test(formData.unitNumber.trim())
      ) {
        newErrors.unitNumber = t("Unit Number must contain only numbers");
      }
      // if (formData.unitNumber?.trim()) {
      //   if (formData.unitNumber.trim().length > 10) {
      //     newErrors.unitNumber = t("Unit Number must not exceed 10 characters");
      //   } else if (!/^[a-zA-Z0-9\-]+$/.test(formData.unitNumber.trim())) {
      //     newErrors.unitNumber = t("Unit Number can only contain letters, numbers and hyphens");
      //   }
      // }
    } else {
      // Street validation
      if (!formData.street.trim()) {
        newErrors.street = t("Street is required");
      } else if (formData.street.trim().length < 3) {
        newErrors.street = t("Street must be at least 3 characters");
      } else if (formData.street.trim().length > 50) {
        newErrors.street = t("Street must not exceed 50 characters");
      }
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = t("City is required");
    } else if (formData.city.trim().length < 2) {
      newErrors.city = t("City must be at least 2 characters");
    } else if (formData.city.trim().length > 50) {
      newErrors.city = t("City must not exceed 50 characters");
    } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.city.trim())) {
      newErrors.city = t(
        "City can only contain letters, spaces, hyphens, dots and apostrophes",
      );
    }

    // State validation
    // if (!formData.state.trim()) {
    //   newErrors.state = t("State is required");
    // } else if (formData.state.trim().length < 2) {
    //   newErrors.state = t("State must be at least 2 characters");
    // } else if (formData.state.trim().length > 50) {
    //   newErrors.state = t("State must not exceed 50 characters");
    // } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.state.trim())) {
    //   newErrors.state = t("State can only contain letters, spaces, hyphens, dots and apostrophes");
    // }

    // Country validation
    if (!formData.country.trim()) {
      newErrors.country = t("Country is required");
    }

    // Zip Code validation
    // if (!formData.zipCode.trim()) {
    //   newErrors.zipCode = t("Zip Code is required");
    // } else if (formData.zipCode.trim().length < 3) {
    //   newErrors.zipCode = t("Zip Code must be at least 3 characters");
    // } else if (formData.zipCode.trim().length > 10) {
    //   newErrors.zipCode = t("Zip Code must not exceed 10 characters");
    // } else if (!/^[a-zA-Z0-9\s\-]+$/.test(formData.zipCode.trim())) {
    //   newErrors.zipCode = t("Zip Code can only contain letters, numbers, spaces and hyphens");
    // }

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = t("Full name is required");
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t("Full name must be at least 2 characters");
    } else if (formData.fullName.trim().length > 100) {
      newErrors.fullName = t("Full name must not exceed 100 characters");
    } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.fullName.trim())) {
      newErrors.fullName = t(
        "Full name can only contain letters, spaces, hyphens, dots and apostrophes",
      );
    } else if (formData.fullName.trim().split(/\s+/).length < 2) {
      newErrors.fullName = t("Please enter both first and last name");
    }

    if (
      formData.isDefault &&
      addresses.some((a) => a.isDefault && a.id !== editingAddress)
    ) {
      err("Please make sure you have one default address!");
    }

    // Phone validation (optional but if provided must be valid)
    if (formData.phone.trim()) {
      // Remove spaces, hyphens, and parentheses for validation
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, "");

      if (!/^\+?[0-9]+$/.test(cleanPhone)) {
        newErrors.phone = t(
          "Phone number can only contain numbers, +, spaces, hyphens and parentheses",
        );
      } else if (cleanPhone.length < 8) {
        newErrors.phone = t("Phone number must be at least 8 digits");
      } else if (cleanPhone.length > 15) {
        newErrors.phone = t("Phone number must not exceed 15 digits");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = <K extends keyof AddressFormData>(
    field: K,
    value: AddressFormData[K],
  ): void => {
    // If country changes, reset dependent fields
    if (field === "country") {
      setFormData({
        ...formData,
        [field]: value,
        // state: "",
        city: "",
        // zipCode: "",
        // Reset Qatar-specific fields if leaving Qatar
        zone: value === "QA" ? formData.zone : "",
        buildingNumber: value === "QA" ? formData.buildingNumber : "",
        unitNumber: value === "QA" ? formData.unitNumber : "",
      });
      setIsManualState(false);
      setIsManualCity(false);
      setIsManualZip(false);
    } else {
      setFormData({ ...formData, [field]: value });
    }

    if (field in errors) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsSaving(true);
      try {
        if (editingAddress) {
          const res = await BaseApi.put(
            `/addresses/${editingAddress}`,
            formData,
          );
          if (res.success && res?.data) {
            success(t("Address updated successfully!"));
            await fetchAddresses();
            await fetchDefaultAddress();
          } else {
            err(t("Failed to update address!"));
          }
        } else {
          const res = await BaseApi.post("/addresses", formData);
          if (res.success && res?.data) {
            success(t("Address created successfully!"));
            await fetchAddresses();
            await fetchDefaultAddress();
          } else {
            err(t("Failed to create address!"));
          }
        }
        closeModal();
      } catch (error) {
        console.error("Error saving address:", error);
        err(t("An error occurred while saving the address"));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await BaseApi.delete(`/addresses/${id}`);
      if (res && res?.success) {
        success(t("Address deleted successfully!"));
        setAddresses(addresses.filter((addr) => addr.id !== id));
        await fetchDefaultAddress();
      } else {
        err(t("Failed to delete address!"));
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      err(t("Failed to delete address!"));
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      const res = await BaseApi.patch(`/addresses/${id}/set-default`);
      if (res.success && res?.data) {
        setAddresses(
          addresses.map((addr) => ({ ...addr, isDefault: addr.id === id })),
        );
        await fetchDefaultAddress();
        success(t("Default address updated!"));
      }
    } catch (error) {
      console.error("Error setting default address:", error);
      err(t("Failed to set default address!"));
    }
  };

  const handleDeleteRequest = (id: number) => {
    openConfirmModal({
      title: t("Delete Address"),
      message: t("Are you sure you want to delete this address?"),
      confirmText: t("Delete"),
      onConfirm: () => handleDelete(id),
    });
  };

  const openConfirmModal = (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    setConfirmConfig({
      isOpen: true,
      title: config.title ?? t("Confirm Action"),
      message: config.message ?? t("Are you sure?"),
      confirmText: config.confirmText ?? "Confirm",
      cancelText: config.cancelText ?? "Cancel",
      onConfirm: () => {
        config.onConfirm();
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  const renderTextField = (
    key:
      | "street"
      | "fullName"
      | "phone"
      | "zone"
      | "buildingNumber"
      | "unitNumber",
    label: string,
    required: boolean = true,
  ) => {
    const maxLengths: Record<typeof key, number> = {
      street: 50,
      // apartment: 50,
      fullName: 100,
      phone: 15,
      zone: 10,
      buildingNumber: 10,
      unitNumber: 10,
    };

    const currentLength = formData[key]?.length || 0;
    const maxLength = maxLengths[key];
    const isNearLimit = currentLength > maxLength * 0.8;

    // Get placeholder based on field
    const getPlaceholder = () => {
      if (key === "zone") return t("Enter Zone Number");
      if (key === "buildingNumber") return t("Enter Building number");
      if (key === "unitNumber") return t("Enter Unit Number (optional)");
      if (key === "street") return t("Enter Street Address");
      return t(`Enter ${label}`);
    };

    // Get helper text
    const getHelperText = () => {
      if (key === "zone" || key === "buildingNumber") return t("Numbers only");
      if (key === "unitNumber") return t("Numbers only (optional)");
      if (key === "phone" && formData[key])
        return t("Format: +974 1234 5678 or 12345678");
      return null;
    };

    return (
      <div>
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
          {required && <span className="text-red-500">*</span>} {label}
          {currentLength > 0 && (
            <span
              className={`float-right text-xs ${isNearLimit ? "text-orange-600" : "text-gray-500"}`}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </label>
        <input
          type="text"
          value={formData[key] || ""}
          onChange={(e) => handleInputChange(key, e.target.value)}
          maxLength={maxLength}
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
            errors[key] ? "border-red-500" : "border-gray-200"
          } focus:border-black focus:outline-none transition-all duration-300`}
          placeholder={getPlaceholder()}
        />
        {errors[key] && (
          <p className="mt-1 text-xs sm:text-sm text-red-500">{errors[key]}</p>
        )}
        {!errors[key] && getHelperText() && (
          <p className="mt-1 text-xs text-gray-500">{getHelperText()}</p>
        )}
        {!errors[key] &&
          key === "fullName" &&
          formData[key] &&
          formData[key]!.trim().split(/\s+/).length === 1 && (
            <p className="mt-1 text-xs text-orange-600">
              {t("Please include both first and last name")}
            </p>
          )}
      </div>
    );
  };

  const renderSelectField = (
    key: "country" | "city",
    label: string,
    options: string[] | Country[],
    required: boolean = true,
  ) => {
    const isCountryField = key === "country";
    const isManualMode =
      // (key === "state" && isManualState) ||
      key === "city" && isManualCity;
    // ||
    // (key === "zipCode" && isManualZip);

    const maxLengths = {
      country: 2,
      state: 50,
      city: 50,
      zipCode: 10,
    };

    const currentLength = formData[key].length;
    const maxLength = maxLengths[key];
    const isNearLimit = currentLength > maxLength * 0.8;

    // Handler for when "other" is selected
    const handleSelectChange = (value: string) => {
      if (value === "other") {
        // if (key === "state") {
        //   setIsManualState(true);
        //   handleInputChange(key, "");
        // } else
        if (key === "city") {
          setIsManualCity(true);
          handleInputChange(key, "");
        }
        // else if (key === "zipCode") {
        //   setIsManualZip(true);
        //   handleInputChange(key, "");
        // }
      } else {
        handleInputChange(key, value);
      }
    };

    // Handler to switch back to dropdown
    const switchToDropdown = () => {
      // if (key === "state") {
      //   setIsManualState(false);
      //   handleInputChange(key, "");
      // } else
      if (key === "city") {
        setIsManualCity(false);
        handleInputChange(key, "");
      }
      // else if (key === "zipCode") {
      //   setIsManualZip(false);
      //   handleInputChange(key, "");
      // }
    };

    return (
      <div>
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
          {required && <span className="text-red-500">*</span>} {label}
          {isManualMode && currentLength > 0 && (
            <span
              className={`float-right text-xs ${isNearLimit ? "text-orange-600" : "text-gray-500"}`}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </label>

        {isManualMode ? (
          <div className="relative">
            <input
              type="text"
              value={formData[key]}
              onChange={(e) => handleInputChange(key, e.target.value)}
              maxLength={maxLength}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
                errors[key] ? "border-red-500" : "border-gray-200"
              } focus:border-black focus:outline-none transition-all duration-300 pr-20`}
              placeholder={t(`Enter ${label}`)}
            />
            <button
              type="button"
              onClick={switchToDropdown}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-semibold bg-[#0d223f] hover:bg-[#0d223f] px-2 sm:px-3 py-1 rounded transition-colors"
            >
              {t("Select")}
            </button>
          </div>
        ) : (
          <select
            value={formData[key]}
            onChange={(e) => handleSelectChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
              errors[key] ? "border-red-500" : "border-gray-200"
            } focus:border-black focus:outline-none transition-all duration-300 bg-white`}
          >
            <option value="">{t(`Select ${label}`)}</option>
            {isCountryField
              ? (options as Country[]).map((country) => (
                  <option key={country.country} value={country.country}>
                    {country.flag} {country.name}
                  </option>
                ))
              : (options as string[]).map((option) => (
                  <option key={option} value={option}>
                    {option === "other" ? t("Other (Enter manually)") : option}
                  </option>
                ))}
          </select>
        )}

        {errors[key] && (
          <p className="mt-1 text-xs sm:text-sm text-red-500">{errors[key]}</p>
        )}

        {!isCountryField && !isManualMode && !errors[key] && !formData[key] && (
          <p className="mt-1 text-xs text-gray-500">
            {t("Can't find your")} {label.toLowerCase()}?{" "}
            {t("Select 'Other' to enter manually")}
          </p>
        )}

        {/* {isManualMode && !errors[key] && (
          <p className="mt-1 text-xs text-blue-600">
            {key === "zipCode" 
              ? t("Enter alphanumeric characters only (e.g., 12345 or AB123)")
              : t("Enter letters only (no numbers or special characters)")
            }
          </p>
        )} */}
      </div>
    );
  };

  /* Loading State */
  if (isLoading) {
    return <LoadingComponent title={t("Addresses")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Address")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {t("Manage your delivery addresses")}
                </p>
              </div>
            </div>

            <button
              onClick={openAddModal}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold">{t("Add a New Address")}</span>
            </button>
          </div>

          {/* Default Address Quick View */}
          {!!defaultAddress && (
            <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border-2 border-[#0d223f]">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-gray-700">
                  {t("Default Address")}
                </span>
              </div>
              <p className="text-gray-800 font-medium">
                {defaultAddress.country === "QA" &&
                !!defaultAddress.zone &&
                !!defaultAddress.buildingNumber ? (
                  <>
                    {t("Zone")} {defaultAddress.zone}, {t("Building")}{" "}
                    {defaultAddress.buildingNumber}
                    {defaultAddress.unitNumber &&
                      `, ${t("Unit")} ${defaultAddress.unitNumber}`}{" "}
                    - {defaultAddress.street} - {defaultAddress.city}
                  </>
                ) : (
                  <>
                    {defaultAddress.street}, {defaultAddress.city}
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Address grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {addresses.map((address, index) => (
            <div
              key={address.id}
              className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 relative"
              style={{
                animation: `fadeIn 0.5s ease-out ${index * 0.1}s backwards`,
              }}
            >
              {/* Default Badge */}
              {address.isDefault && (
                <div
                  className={`absolute top-4 ${i18n.language === "ar" ? "left-4" : "right-4"} bg-orange-500 text-white px-3 py-1 rounded-full flex items-center gap-1 text-xs sm:text-sm font-semibold`}
                >
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-white" />
                  {t("default")}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => openEditModal(address)}
                  className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title={t("Edit")}
                >
                  <Edit2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => handleDeleteRequest(address.id)}
                  className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  title={t("Delete")}
                >
                  <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </button>
              </div>

              {/* Address Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Home className="w-5 h-5 text-gray-600" />
                  <h3 className="text-xl font-bold text-gray-800">
                    {address.fullName}
                  </h3>
                </div>

                {address.country === "QA" &&
                address.zone &&
                address.buildingNumber ? (
                  <>
                    <p className="text-gray-700 mb-1">
                      {t("Zone")}: {address.zone}, {t("Building")}:{" "}
                      {address.buildingNumber}
                      {address.unitNumber &&
                        `, ${t("Unit")}: ${address.unitNumber}`}
                    </p>
                    <p className="text-gray-700 mb-1">{address.street}</p>
                  </>
                ) : (
                  <p className="text-gray-700 mb-1">{address.street}</p>
                )}

                <p className="text-gray-600 mb-3">
                  {address.city}, {address.state},{" "}
                  {COUNTRIES.find((c) => c.country === address.country)?.flag}{" "}
                  {address.country} {address.zipCode}
                </p>
                {address.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <ContactInfo
                      type="phone"
                      value={address.phone}
                      icon={<Phone size={16} />}
                    />
                  </div>
                )}
              </div>

              {/* Set as Default Button */}
              {!address.isDefault && (
                <button
                  onClick={() => handleSetDefault(address.id)}
                  className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg transition-all duration-300 font-semibold text-sm"
                >
                  {t("Set as Default")}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {addresses.length === 0 && !isLoading && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {t("No addresses yet")}
            </h3>
            <p className="text-gray-600 mb-6">
              {t("Add your first delivery address to get started")}
            </p>
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              {t("Add Address")}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Address Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
          style={{ animation: "fadeIn 0.3s ease-out" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s ease-out" }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onBlur={() => validateForm()}
              style={{ animation: "slideDown 0.3s ease-out" }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  {editingAddress ? t("Edit Address") : t("Add a New Address")}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-gray-300 transition-colors p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Country */}
                  {renderSelectField("country", t("Country"), COUNTRIES)}

                  {/* State */}
                  {/* {renderSelectField("state", t("State"), availableStates)} */}

                  {/* City */}
                  {renderSelectField("city", t("City"), availableCities)}

                  {/* Zip Code */}
                  {/* {renderSelectField("zipCode", t("Zip Code"), availableZipCodes)} */}

                  {/* Qatar-specific fields */}
                  {/* {formData.country === "QA" && (
                    <> */}
                  {/* Zone */}
                  {renderTextField("zone", t("Zone"), true)}

                  {/* Street */}
                  {renderTextField("street", t("Street"))}

                  {/* Building Number */}
                  {renderTextField(
                    "buildingNumber",
                    t("Building Number"),
                    true,
                  )}

                  {/* Unit Number */}
                  {renderTextField("unitNumber", t("Unit Number"), false)}
                  {/* </>
                  )} */}

                  {/* Apartment - optional for Qatar */}
                  {/* {renderTextField("apartment", t("Apartment"), formData.country !== "QA")} */}

                  {/* Full Name */}
                  {renderTextField("fullName", t("Full name"))}

                  {/* Phone */}
                  {renderTextField("phone", t("Phone"), false)}
                </div>

                {/* Qatar Address Info Banner */}
                {formData.country === "QA" && (
                  <div className="mt-4 bg-blue-100 border-1 border-blue-300 rounded-lg p-3 flex items-start gap-2">
                    <div className="w-5 h-5 bg-[#0d223f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs sm:text-sm text-blue-800 font-semibold mb-1">
                        {t("Qatar Address Format")}
                      </p>
                      <p className="text-xs text-blue-700">
                        {t(
                          "For Qatar addresses, Zone and Building Number are mandatory. Unit Number is optional.",
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Make Default Checkbox */}
                <div className="mt-6 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) =>
                      handleInputChange("isDefault", e.target.checked)
                    }
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-black cursor-pointer"
                  />
                  <label className="text-xs sm:text-sm text-gray-700">
                    {t("Make this my default address")}
                  </label>
                </div>

                {/* Form Status Indicator */}
                {Object.keys(errors).length === 0 &&
                  formData.street &&
                  formData.city &&
                  formData.fullName &&
                  (formData.country !== "QA" ||
                    (formData.zone && formData.buildingNumber))}
              </div>

              {/* Modal Footer */}
              <div className="px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold text-sm sm:text-base order-2 sm:order-1"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 font-semibold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base order-1 sm:order-2"
                >
                  {isSaving ? (
                    <>
                      <div
                        className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span>{t("Saving...")}</span>
                    </>
                  ) : editingAddress ? (
                    t("Update Address")
                  ) : (
                    t("Add Address")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default AddressManagement;
